<template>
  <div class="waiting-room">
    <v-container fluid>
      <v-row justify="center" align="center" class="fill-height">
        <v-col cols="12" md="8" lg="6">
          <v-card class="pa-8 text-center" elevation="8">
            <!-- Header -->
            <div class="mb-8">
              <v-icon size="80" color="orange" class="mb-4 pulse-animation">
                mdi-clock-time-four-outline
              </v-icon>
              <h1 class="text-h4 mb-4 font-weight-bold">Sala de Espera</h1>
              <p class="text-h6 text-medium-emphasis">Esperando a que el profesor inicie el quiz</p>
            </div>

            <!-- PIN Code Display -->
            <v-chip
              v-if="pinCode"
              size="large"
              color="primary"
              class="mb-6 px-6 py-3"
              style="font-size: 1.2em; font-family: monospace; letter-spacing: 2px"
            >
              <v-icon left>mdi-key-variant</v-icon>
              PIN: {{ pinCode }}
            </v-chip>

            <!-- Waiting Count -->
            <div class="mb-6">
              <div class="text-h5 mb-2">
                <v-icon left color="success">mdi-account-multiple</v-icon>
                {{ waitingCount }}
                {{ waitingCount === 1 ? 'estudiante esperando' : 'estudiantes esperando' }}
              </div>
              <v-progress-linear
                :model-value="Math.min(waitingCount * 10, 100)"
                color="success"
                height="8"
                rounded
                class="mb-2"
              />
            </div>

            <!-- Status Messages -->
            <v-alert v-if="sessionStatus === 'waiting'" type="info" variant="tonal" class="mb-4">
              El profesor iniciará el quiz cuando esté listo
            </v-alert>

            <v-alert
              v-if="error"
              type="error"
              variant="tonal"
              class="mb-4"
              closable
              @click:close="clearError"
            >
              <v-icon left>mdi-alert-circle-outline</v-icon>
              {{ error }}
            </v-alert>

            <!-- Connection Status -->
            <div class="mb-6">
              <v-chip :color="connectionStatus.color" size="small" class="mb-2">
                <v-icon left size="small">{{ connectionStatus.icon }}</v-icon>
                {{ connectionStatus.text }}
              </v-chip>
              <div class="text-caption text-medium-emphasis">
                {{ connectionStatus.description }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success snackbar -->
    <v-snackbar v-model="showSuccessMessage" color="success" timeout="4000" location="top right">
      <v-icon left>mdi-check-circle</v-icon>
      {{ successMessage }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useWebSocket } from '@/composables/useWebSocket'

const router = useRouter()
const store = useStore()
const { connect, disconnect, listenToStudentSession, isConnected: wsConnected } = useWebSocket()

// Reactive data
const showLeaveDialog = ref(false)
const showSuccessMessage = ref(false)
const successMessage = ref('')
const pollingInterval = ref(null)
const websocketChannel = ref(null)

// Computed properties
const sessionStatus = computed(() => store.getters['session/getSessionStatus'])
const participantStatus = computed(() => store.getters['session/getParticipantStatus'])
const pinCode = computed(() => store.getters['session/getPinCode'])
const waitingCount = computed(() => store.getters['session/getWaitingCount'])
const loading = computed(() => store.getters['session/isLoading'])
const error = computed(() => store.getters['session/getError'])
const isWebsocketConnected = computed(() => store.getters['session/isWebsocketConnected'])

const connectionStatus = computed(() => {
  const wsStatus = wsConnected.value || isWebsocketConnected.value
  if (wsStatus) {
    return {
      color: 'success',
      icon: 'mdi-wifi',
      text: 'Conectado',
      description: 'Actualizaciones en tiempo real activas',
    }
  } else {
    return {
      color: 'warning',
      icon: 'mdi-wifi-off',
      text: 'Actualizando...',
      description: 'Verificando estado cada 5 segundos',
    }
  }
})

// Methods
const startPolling = () => {
  if (pollingInterval.value) return

  pollingInterval.value = setInterval(async () => {
    await checkSessionStatus()
  }, 5000) // Poll every 5 seconds

  store.dispatch('session/setPolling', true)
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
    store.dispatch('session/setPolling', false)
  }
}

const checkSessionStatus = async () => {
  const result = await store.dispatch('session/checkSessionStatus')

  if (result.success) {
    // Check if session has started
    if (result.data.session_status === 'active' && sessionStatus.value === 'waiting') {
      successMessage.value = '¡El quiz ha comenzado!'
      showSuccessMessage.value = true

      // Navigate to quiz after a short delay
      setTimeout(() => {
        let groupId = result.data.group_id || store.getters['session/getGroupId']
        if (!groupId) {
          const accessData = store.getters['pins/getAccessData']
          groupId = accessData?.group_id
        }

        if (groupId) {
          console.log('Redirecting to evaluation via polling with groupId:', groupId)
          router
            .push({
              name: 'Evaluation',
              params: { groupId },
            })
            .then(() => {
              console.log('Successfully navigated to evaluation via polling')
            })
            .catch((error) => {
              console.error('Navigation to evaluation via polling failed:', error)
            })
        } else {
          console.error('No group_id available for navigation via polling')
        }
      }, 1500)
    } else if (result.data.session_status === 'completed') {
      // Session was completed
      successMessage.value = 'El quiz ha sido completado'
      showSuccessMessage.value = true

      setTimeout(() => {
        router.push('/pin-access')
      }, 2000)
    }
  }
}

const clearError = () => {
  store.dispatch('session/clearError')
}

const setupWebSocket = () => {
  if (!pinCode.value) return

  try {
    // Connect to WebSocket if not already connected
    if (!wsConnected.value) {
      connect()
    }

    // Listen to student session events
    websocketChannel.value = listenToStudentSession(pinCode.value)

    if (websocketChannel.value) {
      console.log(`WebSocket listening to session: ${pinCode.value}`)
    } else {
      console.log('WebSocket not available, using polling fallback')
    }
  } catch (error) {
    console.error('Failed to setup WebSocket:', error)
  }
}

// Watch for session status changes (WebSocket or polling updates)
watch(sessionStatus, (newStatus, oldStatus) => {
  console.log(`Session status changed from ${oldStatus} to ${newStatus}`)

  if (oldStatus === 'waiting' && newStatus === 'active') {
    console.log('Session transitioned from waiting to active - triggering redirection')

    // Stop polling since session is now active
    stopPolling()

    // Show success message immediately
    successMessage.value = '¡El quiz ha comenzado!'
    showSuccessMessage.value = true

    // Get group_id from session store or access data as fallback
    let groupId = store.getters['session/getGroupId']
    console.log('GroupId from session store:', groupId)

    if (!groupId) {
      const accessData = store.getters['pins/getAccessData']
      groupId = accessData?.group_id
      console.log('GroupId from pins access data:', groupId)
    }

    // Additional fallback - try to get group_id from pin access data
    if (!groupId && pinCode.value) {
      console.log('No groupId found, trying to re-fetch session status')
      // Last resort: re-fetch session status to get group_id
      store
        .dispatch('session/checkSessionStatus')
        .then((result) => {
          if (result.success && result.data.group_id) {
            console.log('GroupId retrieved from checkSessionStatus:', result.data.group_id)
            setTimeout(() => {
              console.log('Redirecting to evaluation (fallback route)')
              router
                .push({
                  name: 'Evaluation',
                  params: { groupId: result.data.group_id },
                })
                .then(() => {
                  console.log('Successfully navigated to evaluation (fallback)')
                })
                .catch((error) => {
                  console.error('Navigation to evaluation failed (fallback):', error)
                })
            }, 1500)
          } else {
            console.error('Failed to retrieve groupId from checkSessionStatus')
          }
        })
        .catch((error) => {
          console.error('checkSessionStatus failed:', error)
        })
      return
    }

    // Proceed with redirection if we have groupId
    if (groupId) {
      setTimeout(() => {
        console.log('Redirecting to evaluation with groupId:', groupId)
        router
          .push({
            name: 'Evaluation',
            params: { groupId },
          })
          .then(() => {
            console.log('Successfully navigated to evaluation')
          })
          .catch((error) => {
            console.error('Navigation to evaluation failed:', error)
            // Try alternative navigation approach
            window.location.href = `/evaluation/${groupId}`
          })
      }, 1500)
    } else {
      console.error('No group_id available for navigation after all fallbacks')
      clearError()
    }
  }

  // Handle case where session goes from null/any to active (late join)
  if (newStatus === 'active' && oldStatus !== 'active') {
    console.log('Session is now active - checking if student joined late')

    let groupId = store.getters['session/getGroupId']
    if (!groupId) {
      const accessData = store.getters['pins/getAccessData']
      groupId = accessData?.group_id
    }

    if (groupId) {
      console.log('Late join detected - redirecting immediately to evaluation')
      successMessage.value = '¡El quiz está en progreso!'
      showSuccessMessage.value = true

      setTimeout(() => {
        router.push({
          name: 'Evaluation',
          params: { groupId },
        })
      }, 1000)
    }
  }
})

// Lifecycle hooks
onMounted(() => {
  // Check if we have session data
  if (!pinCode.value) {
    router.push('/pin-access')
    return
  }

  // Setup WebSocket connection
  setupWebSocket()

  // Start polling as fallback
  startPolling()
})

onUnmounted(() => {
  stopPolling()

  if (websocketChannel.value && pinCode.value) {
    const channelName = `quiz-session.${pinCode.value}`
  }
})
</script>

<style scoped>
.waiting-room {
  min-height: 100vh;
  background: secondary;
  padding: 2rem 0;
}

.fill-height {
  min-height: calc(100vh - 4rem);
}

.pulse-animation {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.v-card {
  backdrop-filter: blur(20px);
  background: rgba(255, 255, 255, 0.95) !important;
}

.v-progress-linear {
  border-radius: 8px;
}

.v-chip {
  font-weight: bold;
  text-transform: uppercase;
}
</style>
